import type { VenueProfileFieldError } from '@/lib/api/errors'

import { BUSINESS_NUMBER_PATTERN, stripNonDigits, type OperationVerificationDocumentState } from './operationVerificationViewModel'
import type { VenueBusinessInfoChangeRequestInput, VenueBusinessInfoView } from './venueBusinessInfoTypes'

/**
 * 사업자 정보 수정 요청 폼 상태 ↔ 요청/응답 매핑 + 전량 수집 클라이언트 검증(#1191, 백엔드 #1189 §D7).
 * `operationVerificationViewModel.ts`와 같은 컨벤션 — `if (status === ...)`를 페이지에 흩지 않는다.
 *
 * 🚨 운영 인증 신청 폼과 **별개 축**이다 — 이 폼은 사업자번호·서류 **둘 다 필수**(전량 교체, 백엔드 D4)라
 * "서류 선택" 문구·검증이 신청 폼과 다르다. 사업자번호 정규화·형식 검증 규칙만 `operationVerificationViewModel.ts`
 * 에서 재사용한다(같은 서버 규칙).
 */

export interface VenueBusinessInfoFormState {
  businessNumber: string
  document: OperationVerificationDocumentState
  requestReason: string
}

export function emptyVenueBusinessInfoFormState(): VenueBusinessInfoFormState {
  return {
    businessNumber: '',
    document: { status: 'idle', fileName: null, documentKey: null, errorMessage: null, previewUrl: null },
    requestReason: '',
  }
}

export const VENUE_BUSINESS_INFO_LIMITS = {
  requestReason: 500,
} as const

const FIELD_LABELS: Record<string, string> = {
  businessNumber: '사업자등록번호',
  documentKey: '사업자등록증',
  requestReason: '수정 사유',
}

/**
 * 제출 버튼 활성 조건 — 사업자번호 입력 + 서류 업로드 **완료**(`document.status === 'done'`) 둘 다
 * 있어야 한다(백엔드 D4 "전량 교체, 둘 다 필수" — 운영 인증 신청 폼과 달리 서류가 선택이 아니다).
 * 🚨 형식 유효성(자릿수 등)은 여기서 보지 않는다 — 제출 클릭 후 `validateVenueBusinessInfoForm`이
 * 필드 오류로 안내한다(`operationVerificationViewModel.ts`와 같은 UX 관례).
 */
export function isVenueBusinessInfoFormComplete(form: VenueBusinessInfoFormState): boolean {
  return form.businessNumber.trim().length > 0 && form.document.status === 'done'
}

/** 클라이언트 사전 검증(api-spec §D7-M1). 모든 위반을 한 번에 수집한다(fail-fast 아님). */
export function validateVenueBusinessInfoForm(form: VenueBusinessInfoFormState): VenueProfileFieldError[] {
  const errors: VenueProfileFieldError[] = []

  const businessNumberDigits = stripNonDigits(form.businessNumber)
  if (businessNumberDigits.length === 0) {
    errors.push({
      field: 'businessNumber',
      code: 'REQUIRED',
      message: `${FIELD_LABELS.businessNumber}을(를) 입력해 주세요.`,
    })
  } else if (!BUSINESS_NUMBER_PATTERN.test(businessNumberDigits)) {
    errors.push({
      field: 'businessNumber',
      code: 'INVALID_FORMAT',
      message: '사업자등록번호는 숫자 10자리로 입력해 주세요. (예: 000-00-00000)',
    })
  }

  // 🚨 서류는 이 폼에서 선택이 아니다(D4) — 업로드 완료 전이면 REQUIRED. 업로드 자체의 확장자·용량
  // 오류는 `OperationVerificationDocumentField`가 `document.status === 'error'`로 이미 표시한다.
  if (form.document.status !== 'done') {
    errors.push({
      field: 'documentKey',
      code: 'REQUIRED',
      message: `${FIELD_LABELS.documentKey}을(를) 첨부해 주세요.`,
    })
  }

  const reason = form.requestReason.trim()
  if (reason.length > VENUE_BUSINESS_INFO_LIMITS.requestReason) {
    errors.push({
      field: 'requestReason',
      code: 'TOO_LONG',
      message: `${FIELD_LABELS.requestReason}은(는) 최대 ${VENUE_BUSINESS_INFO_LIMITS.requestReason}자까지 입력할 수 있습니다.`,
    })
  }

  return errors
}

/** 빈 문자열/공백뿐인 문자열은 미입력(`null`)으로 정규화한다(`operationVerificationViewModel.ts`와 동일 규칙). */
function toNullableText(value: string): string | null {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * 폼 → `POST` 요청 바디. 검증(`validateVenueBusinessInfoForm`)을 통과한 뒤에만 호출한다 — 서류는
 * 항상 `document.status === 'done'`(검증이 이를 강제)이라 `documentKey`/`fileSize`가 non-null임이
 * 보장된다.
 */
export function toVenueBusinessInfoChangeRequestInput(
  form: VenueBusinessInfoFormState
): VenueBusinessInfoChangeRequestInput {
  if (form.document.status !== 'done' || !form.document.documentKey || !form.document.fileName) {
    throw new Error('서류 업로드가 완료되지 않은 상태에서는 제출 요청을 만들 수 없습니다.')
  }

  return {
    businessNumber: stripNonDigits(form.businessNumber),
    documentKey: form.document.documentKey,
    documentFileName: form.document.fileName,
    documentSize: form.document.fileSizeBytes ?? 0,
    requestReason: toNullableText(form.requestReason),
  }
}

/** 대기 중인 요청이 있는지 — 있으면 수정 폼을 열지 않는다(요구사항 3, 중복 제출 1차 방어). */
export function hasPendingBusinessInfoChangeRequest(view: VenueBusinessInfoView): boolean {
  return view.pendingRequest !== null
}
